'use client';

import { createRevealObserver } from '@/lib/reveal-observer';
import React, { useEffect, useRef } from 'react';

const perspectives = [
  {
    label: 'Founder Perspective',
    desc: 'We understand the pressure of raising capital while building a company. The time constraints. The emotional weight. The competing priorities.',
  },
  {
    label: 'Investor Perspective',
    desc: 'We understand the questions investors need answered before committing capital — and how to present opportunities in the language investors speak.',
  },
  {
    label: 'Network Perspective',
    desc: 'We understand that the value of an introduction depends on relevance. A warm introduction to the wrong investor is still the wrong conversation.',
  },
  {
    label: 'Strategic Perspective',
    desc: 'We look beyond the immediate round and consider what type of capital partner the company needs for the next stage of its journey.',
  },
];

export default function WhatMakesDifferentSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = createRevealObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-fade').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="different" className="py-24 px-6 border-t border-primary/10">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left — sticky text column */}
          <div className="lg:sticky lg:top-32">
            <p className="section-label scroll-fade mb-5">What Makes Us Different</p>
            <h2 className="text-section-title font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1 mb-8">
              We understand{' '}
              <span className="font-serif italic text-primary">both sides of the table.</span>
            </h2>
            <p className="text-base text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-2 mb-8">
              Aregn Capital is built around experience working with both founders and investors. We
              understand that founders need capital. But investors need something more:
            </p>

            <div className="scroll-fade scroll-fade-delay-3 border border-primary/25 rounded-2xl p-8 bg-primary/5">
              <p className="font-serif italic text-3xl sm:text-4xl text-foreground leading-tight mb-4">
                &quot;Conviction.&quot;
              </p>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                Conviction comes from understanding the opportunity, believing in the market,
                trusting the team and seeing a credible path to value creation. Our job is to help
                bridge that gap.
              </p>
            </div>
          </div>

          {/* Right — perspectives */}
          <div className="space-y-4">
            {perspectives?.map((p, i) => (
              <div
                key={p?.label}
                className={`bento-card rounded-2xl p-6 scroll-fade`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">{p?.label}</h3>
                    <p className="text-sm text-muted-foreground font-light leading-relaxed">
                      {p?.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
